import React, { useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { PinComment } from "../../Chat";
import { hexToRGB } from "../../../../utils";
import { format } from "date-fns";
import { Minimise, Maximise, Bin } from "images/dynamic-svgs";
import { useAuth } from "firebase";

function SubmittedQuestion({
  isAdmin,
  submittedQuestion,
  selectedQuestion,
  setSelectedQuestion,
  formValues,
  index,
  userQuestions,
}) {
  const [minimisedQuestions, setMinimisedQuestions] = useState([]);
  const { firebase } = useAuth();

  const handleQuestionSelection = () => {
    if (isAdmin) {
      if (selectedQuestion?.qid === submittedQuestion.qid) {
        setSelectedQuestion(null);
      } else {
        setSelectedQuestion(submittedQuestion);
      }
    }
  };

  const handleDeleteQuestion = ({ qid, uid }) => {
    firebase.deleteQuestion({ qid, uid });
  };

  const handleMinMaxQuestion = ({ e, qid }) => {
    e.stopPropagation();
    if (minimisedQuestions.includes(qid)) {
      setMinimisedQuestions(
        minimisedQuestions.filter((question) => question !== qid)
      );
    } else {
      setMinimisedQuestions([...minimisedQuestions, qid]);
    }
  };

  const variants = {
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        height: {
          duration: 0.4,
        },
        opacity: {
          duration: 0.25,
          delay: 0.15,
        },
      },
    },
    hidden: {
      height: 0,
      opacity: 0,
      transition: {
        height: {
          duration: 0.4,
        },
        opacity: {
          duration: 0.25,
        },
      },
    },
  };

  return (
    <Wrapper
      layout="position"
      layoutDependency={index}
      isModerator={isAdmin}
      isSelected={selectedQuestion?.qid === submittedQuestion.qid}
      isMinimised={minimisedQuestions.includes(submittedQuestion.qid)}
      onClick={handleQuestionSelection}
    >
      <QuestionContainer>
        {isAdmin && (
          <SelectQuestionCheckbox
            checked={selectedQuestion?.qid === submittedQuestion.qid}
            id="selectQuestionCheckbox"
            type="checkbox"
            readOnly
          />
        )}
        <p>
          <span>
            {formValues.uid === submittedQuestion.uid
              ? `My Question${
                  userQuestions.length > 1
                    ? ` (${
                        userQuestions.findIndex(
                          (question) => question.qid === submittedQuestion.qid
                        ) + 1
                      })`
                    : ""
                }`
              : submittedQuestion.name
              ? `${submittedQuestion.name}`
              : "Anonymous User"}
          </span>
          <Actions>
            {isAdmin && (
              <MinMaxQuestion
                onClick={(e) =>
                  handleMinMaxQuestion({ e, qid: submittedQuestion.qid })
                }
              >
                {minimisedQuestions.includes(submittedQuestion.qid) ? (
                  <Maximise />
                ) : (
                  <Minimise />
                )}
              </MinMaxQuestion>
            )}
            <DeleteQuestion
              isAdmin={isAdmin}
              onClick={(e) => {
                e.preventDefault();
                handleDeleteQuestion({
                  qid: submittedQuestion.qid,
                  uid: submittedQuestion.uid,
                });
              }}
            >
              <Bin />
            </DeleteQuestion>
          </Actions>
        </p>
        <AnimatePresence>
          {!minimisedQuestions.includes(submittedQuestion.qid) && (
            <>
              <motion.p
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={variants}
              >
                {submittedQuestion.text}
              </motion.p>
              {submittedQuestion.timestamp && (
                <motion.p
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={variants}
                >
                  <span>
                    {format(submittedQuestion.timestamp.toDate(), "HH:mmaaa")}
                  </span>
                </motion.p>
              )}
            </>
          )}
        </AnimatePresence>
      </QuestionContainer>
      <AnimatePresence initial={false}>
        {submittedQuestion?.answer &&
          !minimisedQuestions.includes(submittedQuestion.qid) && (
            <ModeratorsAnswer
              isModerator={isAdmin}
              isSelected={selectedQuestion?.qid === submittedQuestion.qid}
            >
              <p>
                <span>Moderator&apos;s Answer:</span>
              </p>
              <p>{submittedQuestion.answer.text}</p>
              <p>
                {submittedQuestion.answer.timestamp && (
                  <span>
                    {format(
                      submittedQuestion.answer.timestamp.toDate(),
                      "HH:mmaaa"
                    )}
                  </span>
                )}
              </p>
            </ModeratorsAnswer>
          )}
      </AnimatePresence>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div).attrs({
  initial: {
    opacity: 0,
    height: 0,
  },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      height: {
        duration: 0.4,
      },
      opacity: {
        duration: 0.25,
        delay: 0.15,
      },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: {
        duration: 0.4,
      },
      opacity: {
        duration: 0.25,
      },
    },
  },
})`
  background-color: ${({ isSelected }) =>
    isSelected ? hexToRGB({ color: "#2a6440", alpha: 0.2 }) : "transparent"};
  color: ${({ isSelected }) => (isSelected ? "var(--abbvie-green)" : "#000")};
  cursor: ${({ isModerator }) => (isModerator ? "pointer" : "auto")};
  font-size: 1rem;
  margin-bottom: 0.688em;
  padding: 0.5em;
  position: relative;
  box-sizing: border-box;
  transition: background-color 200ms ease-in-out, color 200ms ease-in-out;
  p {
    padding-left: ${({ isModerator }) => (isModerator ? "1.375rem" : 0)};
  }
  p:first-of-type {
    width: 100%;
    align-items: flex-end;
    display: flex;
    justify-content: space-between;
    margin-bottom: ${({ isMinimised }) => (isMinimised ? 0 : "0.313em")};

    span:first-of-type {
      font-weight: 700;
    }
  }

  p:nth-of-type(3) {
    width: auto;
    align-items: flex-end;
    display: flex;
    margin-top: 0.375rem;

    span {
      color: ${({ isSelected }) => (isSelected ? "#000" : "#c4c4c4")};
      transition: color 200ms ease-in-out;
      font-size: 0.75em;
      position: ${({ isModerator }) => (isModerator ? "relative" : "absolute")};
      top: ${({ isModerator }) => (isModerator ? 0 : "0.4em")};
      right: 0;
    }
  }
  small {
    color: #000;
    display: inline-block;
    font-size: 0.65rem;
    left: 0.95em;
    position: relative;
  }
`;

const QuestionContainer = styled.div`
  position: relative;
`;

export const SelectQuestionCheckbox = styled.input`
  appearance: none;
  background-color: transparent;
  border: 1px solid #bdbdbd;
  cursor: pointer;
  height: 1rem;
  margin-top: 2px;
  outline: none;
  position: absolute;
  transition: all 0.1s;
  width: 1rem;
  &:focus {
    border: 2px solid var(--abbvie-green);
  }
  &:checked {
    border: 1px solid var(--abbvie-green);
  }
  &:checked:before {
    color: var(--abbvie-green);
    content: "✓";
    font-size: 0.8rem;
    height: 0.8rem;
    left: 50%;
    outline: none;
    position: absolute;
    top: 50%;
    transform: translate(-37%, -60%);
    width: 0.8rem;
  }
`;

const ModeratorsAnswer = styled(motion.div).attrs({
  initial: {
    opacity: 0,
    height: 0,
  },
  animate: {
    opacity: 1,
    height: "auto",
    transition: {
      type: "tween",
      ease: "easeInOut",
      height: {
        duration: 0.4,
      },
      opacity: {
        duration: 0.65,
        delay: 0.1,
      },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      type: "tween",
      ease: "easeInOut",
      height: {
        duration: 0.4,
        delay: 0.55,
      },
      opacity: {
        duration: 0.65,
      },
    },
  },
})`
  color: ${({ isSelected }) => (isSelected ? "#000" : "#c4c4c4")};
  cursor: ${({ isModerator }) => (isModerator ? "pointer" : "auto")};
  margin-top: 0.625rem;
  padding: 0.125rem 0 0.25rem 1rem;
  transition: color 200ms ease-in-out;
  position: ${({ isModerator }) => (isModerator ? "unset" : "relative")};

  p:nth-of-type(3) span {
    top: ${({ isModerator }) => (isModerator ? 0 : "0.6em")};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-flow: nowrap;
  gap: 0.313em;
  margin-right: 0.5rem;
`;

const MinMaxQuestion = styled(PinComment)`
  margin-left: 0;
  > svg {
    bottom: unset;
    left: unset;
    position: relative;
  }
`;

const DeleteQuestion = styled(PinComment)`
  align-self: center;
  margin-left:  auto;
  margin-bottom: ${({ isAdmin }) => (isAdmin ? "0.25rem" : "-1.75rem")};
  > svg {
    fill: var(--abbvie-green);
    height: 1em;
    left: unset;
    position: relative;
    width: 1em;
  }
`;

export default SubmittedQuestion;
